import React, { useMemo } from "react";
import styles from "../styles/menus-evento.module.css";
import { IoPrintOutline } from "react-icons/io5";
import { fmtLongDate } from "../domain/dateUtils";
import { ordenarPlatosDeMenu } from "../domain/menusUtil";

const vinoLabel = (v) => {
    if (!v) return "—";
    const nombre = v.nombre || `Vino #${v.id ?? "—"}`;
    const tipo = v.tipo ? ` • ${v.tipo}` : "";
    return `${nombre}${tipo}`;
};

export default function MenuItem({
    evento,
    menu,
    idx,
    open,
    onToggle,
    vinosCatalogo = [], // <-- NUEVO (opcional)
}) {
    const platosOrdenados = useMemo(() => ordenarPlatosDeMenu(menu), [menu]);

    const vinosIndex = useMemo(() => {
        const arr = Array.isArray(vinosCatalogo) ? vinosCatalogo : [];
        return new Map(arr.map((v) => [Number(v.id), v]));
    }, [vinosCatalogo]);

    const vinosDelMenu = useMemo(() => {
        // Caso A: vienen objetos
        const vinosObj = Array.isArray(menu?.vinos) ? menu.vinos : [];

        // Caso B: vienen ids
        const ids =
            Array.isArray(menu?.vinoIds) ? menu.vinoIds :
                Array.isArray(menu?.vinosIds) ? menu.vinosIds :
                    Array.isArray(menu?.vinosIdsSeleccionados) ? menu.vinosIdsSeleccionados :
                        [];

        // Caso C (opcional): por etapa si existiera (ap/pr/po)
        const porEtapa = menu?.vinosPorEtapa || null;

        if (porEtapa && typeof porEtapa === "object") {
            const stages = [
                ["ap", "Aperitivo"],
                ["pr", "Primer plato"],
                ["po", "Postre"],
            ];

            return stages.flatMap(([key, label]) => {
                const list = Array.isArray(porEtapa[key]) ? porEtapa[key] : [];
                return list.map((x) => {
                    const id = typeof x === "object" ? x?.id : x;
                    const obj = typeof x === "object" ? x : vinosIndex.get(Number(id));
                    return { id: Number(id), etapa: label, vino: obj || null };
                });
            });
        }

        if (vinosObj.length) {
            return vinosObj.map((v) => ({ id: Number(v.id), etapa: null, vino: v }));
        }

        if (ids.length) {
            return ids.map((id) => ({
                id: Number(id),
                etapa: null,
                vino: vinosIndex.get(Number(id)) || null,
            }));
        }

        return [];
    }, [menu, vinosIndex]);

    const resumenMenu = [
        fmtLongDate(evento?.fechaInicio), `${menu?.comensales} comensales`, menu?.lugar,
    ]
        .filter(Boolean)
        .join(" • ");

    return (
        <li className={`${styles["menu-item"]} ${open ? styles.open : ""}`}>
            <div className={styles["menu-head"]}>
                <button
                    className={styles["menu-toggle"]}
                    onClick={onToggle}
                    aria-expanded={open}
                >
                    <span className={styles.chev}>▾</span>
                    <div className={styles["menu-title"]}>
                        <strong>{menu?.nombre || `Menú ${idx + 1}`}</strong>
                        <span className={styles.muted}>
                            {" — "}
                            {resumenMenu}
                        </span>
                    </div>
                </button>

                <div className={styles["menu-actions"]}>
                    <button
                        type="button"
                        className={`${styles.button} ${styles["secondary-button"]}`}
                        onClick={() => {
                            const lang = menu?._lang || "es";
                            const url = `/print/menu?eventId=${evento.id}&menuId=${menu.id}&lang=${lang}`;
                            window.open(url, "_blank", "noopener,noreferrer");
                        }}
                    >
                        <IoPrintOutline style={{ fontSize: "15px" }} /> Imprimir
                    </button>
                </div>
            </div>

            {open && (
                <div className={styles["menu-body"]}>
                    {/* PLATOS */}
                    <div style={{ marginBottom: 10 }}>
                        <strong>Platos</strong>
                        {platosOrdenados.length > 0 ? (
                            <ul className={styles.chips}>
                                {platosOrdenados.map((plato) => (
                                    <li key={plato.id ?? plato.nombre} className={styles.chip}>
                                        {plato.nombre || `Plato ${plato.id}`}
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <div className={styles["empty-inline"]}>Menú sin platos.</div>
                        )}
                    </div>

                    {/* VINOS */}
                    <div>
                        <strong>Vinos</strong>
                        {vinosDelMenu.length > 0 ? (
                            <ul className={styles.chips}>
                                {vinosDelMenu.map((row) => (
                                    <li
                                        key={`${row.id}-${row.etapa || "x"}`}
                                        className={styles.chip}
                                        title={row.vino ? vinoLabel(row.vino) : `Vino #${row.id}`}
                                    >
                                        {row.etapa ? <strong>{row.etapa}:</strong> : null}{" "}
                                        {row.vino ? vinoLabel(row.vino) : `Vino #${row.id}`}
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <div className={styles["empty-inline"]}>Menú sin vinos.</div>
                        )}
                    </div>
                </div>
            )}
        </li>
    );
}
